/**
 * Cart Progress Bar Custom Element
 * Handles automatic adding/removing of free gift product when cart threshold is reached
 */

// Global state to prevent multiple instances from conflicting
window.CartProgressBarState = window.CartProgressBarState || {
  isProcessing: false,
  initialized: false,
  freeGiftVariantId: null,
  freeGiftThreshold: 0,
};

class CartProgressBar extends HTMLElement {
  constructor() {
    super();
    this.freeGiftVariantId = this.dataset.freeGiftVariantId;
    this.freeGiftThreshold = parseInt(this.dataset.freeGiftThreshold, 10) || 0;
  }

  get state() {
    return window.CartProgressBarState;
  }

  get cartDrawer() {
    return document.querySelector('cart-drawer');
  }

  get sectionId() {
    return this.cartDrawer?.dataset?.sectionId || 'cart-drawer';
  }

  get sections() {
    return [this.sectionId, 'cart-icon-bubble'];
  }

  get cartDrawerItems() {
    return this.cartDrawer?.querySelector('cart-drawer-items');
  }

  setLoading(isLoading) {
    this.cartDrawerItems?.classList.toggle('is-loading', isLoading);
  }

  connectedCallback() {
    if (!this.freeGiftVariantId || !this.freeGiftThreshold || this.state.initialized) {
      return;
    }

    this.state.initialized = true;
    this.state.freeGiftVariantId = this.freeGiftVariantId;
    this.state.freeGiftThreshold = this.freeGiftThreshold;

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source !== 'cart-progress-bar') {
        this.checkAndUpdateFreeGift();
      }
    });

    this.checkAndUpdateFreeGift();
  }

  async checkAndUpdateFreeGift() {
    if (this.state.isProcessing) {
      return;
    }

    this.state.isProcessing = true;

    try {
      const cartData = await this.fetchCart();
      const variantId = parseInt(this.state.freeGiftVariantId, 10);
      const threshold = this.state.freeGiftThreshold;

      const hasFreeGift = cartData.items?.some((item) => item.variant_id === variantId);
      const cartTotal = this.calculateCartTotal(cartData, variantId);
      const hasReachedThreshold = cartTotal >= threshold;

      if (hasReachedThreshold && !hasFreeGift) {
        await this.addFreeGiftToCart(variantId);
      } else if (!hasReachedThreshold && hasFreeGift) {
        await this.removeFreeGiftFromCart(cartData, variantId);
      }
    } catch (error) {
      console.error('CartProgressBar: Error checking cart state', error);
    } finally {
      this.state.isProcessing = false;
    }
  }

  async fetchCart() {
    const response = await fetch(`${routes.cart_url}.js`);
    return response.json();
  }

  calculateCartTotal(cartData, excludeVariantId) {
    return (
      cartData.items?.reduce((total, item) => {
        return item.variant_id === excludeVariantId ? total : total + item.final_line_price;
      }, 0) || 0
    );
  }

  async addFreeGiftToCart(variantId) {
    this.setLoading(true);

    try {
      const body = JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: { _free_gift: 'true' } }],
        sections: this.sections,
        sections_url: window.location.pathname,
      });

      const response = await fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body });
      const responseData = await response.json();

      if (responseData.status) {
        console.error('CartProgressBar: Failed to add free gift', responseData);
        return;
      }

      this.updateCartDrawerUI(responseData);
    } catch (error) {
      console.error('CartProgressBar: Error adding free gift to cart', error);
    } finally {
      this.setLoading(false);
    }
  }

  async removeFreeGiftFromCart(cartData, variantId) {
    this.setLoading(true);
    try {
      const lineIndex = cartData.items.findIndex((item) => item.variant_id === variantId);
      if (lineIndex === -1) {
        return;
      }

      const body = JSON.stringify({
        line: lineIndex + 1,
        quantity: 0,
        sections: this.sections,
        sections_url: window.location.pathname,
      });

      const response = await fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body });
      const responseData = await response.json();

      if (responseData.errors) {
        console.error('CartProgressBar: Failed to remove free gift', responseData);
        return;
      }

      this.updateCartDrawerUI(responseData);
    } catch (error) {
      console.error('CartProgressBar: Error removing free gift from cart', error);
    } finally {
      this.setLoading(false);
    }
  }

  updateCartDrawerUI(responseData) {
    const { cartDrawer } = this;

    if (cartDrawer?.renderContents) {
      cartDrawer.renderContents(responseData);
    }

    if (cartDrawer) {
      const isEmpty = responseData.item_count === 0;
      cartDrawer.classList.toggle('is-empty', isEmpty);

      const cartDrawerItems = cartDrawer.querySelector('cart-drawer-items');
      cartDrawerItems?.classList.toggle('is-empty', isEmpty);
    }

    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'cart-progress-bar',
      cartData: responseData,
    });
  }
}

if (!customElements.get('cart-progress-bar')) {
  customElements.define('cart-progress-bar', CartProgressBar);
}
